/**
 * PATH: packages/ui/src/shortcuts/types.ts
 * FILE_ID: UI-SHORTCUTS-TYPES-001
 * PURPOSE: Machine-readable type model for the universal keyboard-shortcut
 *          system — scoped action descriptors, normalized bindings, and the
 *          user-override shape persisted via /api/settings/ui/.
 * WHY: The legacy registry (registry.ts) stores DISPLAY strings only, so
 *      nothing could be matched against a KeyboardEvent, scoped per screen,
 *      or remapped by users. These types are the single source of truth the
 *      dispatcher (dispatcher.ts) and the read-only reference both consume.
 *
 * FLOW:
 *   ┌──────────────────┐   ┌──────────────┐   ┌────────────────────────┐
 *   │ ShortcutAction[]  │ → │ + overrides  │ → │ EffectiveShortcut[]     │
 *   │ (defaults)        │   │ (per user)   │   │ (binding + enabled)     │
 *   └──────────────────┘   └──────────────┘   └────────────────────────┘
 *
 * RELATED:
 *   - ./binding.ts: parse/match/format a Binding string
 *   - ./overrides.ts: fold ShortcutOverrideMap onto actions
 *   - ./registry.ts: legacy display registry + ShortcutHubId
 */

import type { ShortcutHubId } from './registry'

/**
 * Where an action is active. Precedence at dispatch is screen > hub > global
 * (see dispatcher.ts). A 'hub'/'screen' action only fires when the current
 * route context matches.
 */
export type ShortcutScope =
  | { kind: 'global' }
  | { kind: 'hub'; hub: ShortcutHubId }
  | { kind: 'screen'; screen: string }

/**
 * A normalized, platform-agnostic binding string.
 *   chord:    'mod+k', 'shift+f', 'ctrl+g'  (mod = ⌘ on macOS, Ctrl elsewhere)
 *   sequence: 'g i'  (press g then i — parsed/displayed, NOT dispatched in v1)
 * Stored verbatim in ui_preferences and rendered via formatBinding().
 */
export type Binding = string

export interface ShortcutAction {
  /** Stable, namespaced id: 'app.commandPalette', 'quote.save'. The contract. */
  id: string
  scope: ShortcutScope
  /** Default key combo. null = documented but has no bound key (doc-only row). */
  defaultBinding: Binding | null
  description: string
  /** Sub-grouping within a scope (reuses the legacy category labels). */
  category?: string
  /**
   * Display override for rows whose visual keys differ from a single formatted
   * binding (e.g. the doc-only "↑ ↓ move through results" row). When present,
   * the reference renders these instead of formatBinding(defaultBinding).
   */
  displayKeys?: string[]
  /** false = locked: not user-editable / reserved (e.g. Escape). Default true. */
  editable?: boolean
  /** Allow firing while a text input/textarea/contenteditable is focused. */
  allowInInput?: boolean
  /**
   * Dispatch on the CAPTURE phase (before browser defaults + any downstream
   * listener) and stopPropagation on match, making this the single authority
   * for its chord — e.g. ⌘F must beat the browser Find bar and any in-page
   * find surface it didn't claim. Default false = bubble-phase dispatch.
   */
  capture?: boolean
}

/** Per-user customization for one action id. Absent fields = use default. */
export interface ShortcutOverride {
  /** Remapped binding; null = explicitly unbound; undefined = use default. */
  binding?: Binding | null
  /** false = user disabled it; undefined/true = enabled. */
  enabled?: boolean
}

/** actionId → override. Mirrors the `shortcut_overrides` ui_preferences key. */
export type ShortcutOverrideMap = Record<string, ShortcutOverride>

/** An action with its user-effective binding + enabled state resolved. */
export interface EffectiveShortcut {
  action: ShortcutAction
  /** Effective binding after applying the override (may be null = unbound). */
  binding: Binding | null
  enabled: boolean
}

/** Runtime platform used to resolve `mod` and to render keycaps. */
export type Platform = 'mac' | 'other'

/**
 * Display platform: 'cross' renders the legacy single cross-platform token
 * ('⌘/Ctrl') so the read-only reference output is byte-identical to before.
 */
export type DisplayPlatform = Platform | 'cross'

/** Current route context the dispatcher matches scoped actions against. */
export interface ShortcutContext {
  hub?: ShortcutHubId
  screen?: string
}

/** localStorage namespace shared with useShortcutsHubTab (the precedent). */
export const SHORTCUT_STORAGE_PREFIX = 'quotech-ui-pref:'

/**
 * localStorage key for the persisted override map. Shared by the settings
 * editor that WRITES it (useShortcutOverrides) and the app-shell reader that
 * feeds the dispatcher (useShortcutOverridesRuntime), so both agree on one key.
 */
export const SHORTCUT_OVERRIDES_STORAGE_KEY = `${SHORTCUT_STORAGE_PREFIX}shortcut_overrides`

/**
 * Same-tab broadcast fired after the override map is written to localStorage.
 * The native `storage` event only fires in OTHER tabs, so the editor dispatches
 * this on `window` to let the runtime dispatcher reader re-read immediately.
 */
export const SHORTCUT_OVERRIDES_CHANGED_EVENT = 'quotech:shortcut-overrides-changed'
